import { ReverifyRequest, VerificationStatus } from "shared/types";
import { useMutation } from "@tanstack/react-query";
import { instance } from "api/apiBase";
import { toast } from "react-toastify";
import { getErrorMessage } from "utils/error";
import { CombinedIncome } from "views/applications-id-report/slices/CombinedIncomeReportSlice/types/income";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  AlertCircleIcon,
  Loading03Icon
} from "@hugeicons-pro/core-stroke-rounded";
import { Button, ButtonVariant, Show } from "shared/components";
import { ConfirmDialog } from "shared/components/modals/ConfirmDialog";
import { cx } from "shared/utils";
import { DateTime } from "luxon";
import { useState } from "react";
import { useCombinedIncomeContext } from "views/applications-id-report/slices/CombinedIncomeReportSlice/providers/useCombinedIncome";

const getManualAdditionHelpText = (income: CombinedIncome) => {
  const author = income.added_by?.name ?? "a partner";
  const date = DateTime.fromISO(income.created_at).toFormat(
    "LLL d, yyyy', at' h:mm a"
  );

  return (
    <>
      Income source was added by {author}
      <br />
      on {date}
    </>
  );
};

interface Props {
  income: CombinedIncome;
  applicationId?: number | string;
  verificationId?: number | string;
  applicantId?: number | string;
  pendingReverifyRequest?: ReverifyRequest | null;
  verificationStatus?: VerificationStatus;
  onCancelled?: () => void;
}

export const ProcessingStatus = ({ income }: Props) => {
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const {
    applicationId,
    applicantId,
    currentlySelectedVerificationId,
    refetch
  } = useCombinedIncomeContext();

  const cancelManualIncomeMutation = useMutation({
    mutationKey: [
      "MANUAL_INCOME",
      "DELETE",
      String(applicationId),
      String(currentlySelectedVerificationId),
      income.id
    ],
    mutationFn: () =>
      instance.post(
        `portal/v1/applications/${applicationId}/verifications/${currentlySelectedVerificationId}/income/${income.id}/cancel_manual?applicant_id=${applicantId}`
      ),
    onSuccess: () => {
      toast.success("Income verification canceled");
      setCancelModalOpen(false);
      refetch();
    },
    onError: (err) => {
      toast.error(getErrorMessage(err, "Failed to cancel income verification"));
    }
  });

  if (income.status !== "processing") return null;

  const manuallyAddedIncome = Boolean(income.additional_data?.manually_added);

  const cancelIncomeVerification = () => {
    if (!manuallyAddedIncome) return;

    cancelManualIncomeMutation.mutate();
  };

  return (
    <div
      className={cx(
        "flex flex-col items-center justify-center min-h-[250px] w-full mx-auto",
        manuallyAddedIncome ? "mt-6" : ""
      )}
    >
      <div className="size-16 shrink-0 bg-warning-50 rounded-full flex items-center justify-center mb-6 mx-auto">
        <HugeiconsIcon
          icon={Loading03Icon}
          size={24}
          className="text-warning-600"
        />
      </div>

      <h2 className="text-base font-bold text-content-500 mb-2 text-center">
        {manuallyAddedIncome
          ? "Verification request in progress"
          : "Verifying income source with documents..."}
      </h2>

      <p className="text-center text-sm font-medium text-content-300 mb-4">
        {manuallyAddedIncome
          ? getManualAdditionHelpText(income)
          : "Verification may take up to an hour"}
      </p>

      <Show if={manuallyAddedIncome}>
        <Button
          variant={ButtonVariant.BASE}
          className="mt-2 mb-5"
          onClick={() => setCancelModalOpen(true)}
        >
          Cancel
        </Button>
      </Show>

      <ConfirmDialog
        opened={cancelModalOpen}
        onClose={() => setCancelModalOpen(false)}
        onConfirm={cancelIncomeVerification}
        variant="danger"
        icon={{ icon: AlertCircleIcon }}
        title="Cancel income verification"
        description="You're about to cancel the new income source verification. You won't be charged for the incomplete report."
        confirmButton={{
          label: "Cancel verification",
          loading: cancelManualIncomeMutation.isPending
        }}
        hideCancelButton
        showCloseButton
      />
    </div>
  );
};
